import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';

interface SidePanel {
    name: string | null;
    isOpen: boolean;
    data?: Record<string, any>;
}

interface SidePanelState {
    sidePanel: Array<SidePanel>;
}

const initialState: SidePanelState = {
    sidePanel: [],
};

type OpenSidePanelPayload = string | {
    name: string;
    data?: Record<string, any>;
};

/*
    The idea of this state is to keep track of the side panels that are open and closed.
    This way we can have multiple side panels open at the same time.
    This does not handle opening and closing the side panels, that is done in the component, we
    only keep track of the state of the side panels, as we need to move content around when a side panel is open.

    Example of the state:
    SidePanelState: {
        sidePanel: [
            {
                name: 'sidePanel1',
                isOpen: true,
            },
            {
                name: 'sidePanel2',
                isOpen: false,
            },
        ]
    }
*/
export const sidePanelSlice = createSlice({
    name: 'sidePanelSlice',
    initialState,
    reducers: {
        openSidePanel: (state, action: PayloadAction<OpenSidePanelPayload>) => {
            let name: string;
            let data: Record<string, any> | undefined;
            if (typeof action.payload === 'string') {
                name = action.payload;
                data = {};
            } else {
                name = action.payload.name;
                data = action.payload.data || {};
            }
            const sidePanel = state.sidePanel.find((panel) => panel.name === name);
            if (sidePanel) {
                const dataChanged = !isEqual(sidePanel.data, data);
                const wasClosed = !sidePanel.isOpen;
                if (wasClosed || dataChanged) {
                    sidePanel.isOpen = true;
                    sidePanel.data = data;
                }
            } else {
                state.sidePanel.push({
                    name,
                    isOpen: true,
                    data,
                });
            }
        },
        closeSidePanel: (state, action: PayloadAction<string>) => {
            const sidePanel = state.sidePanel.find((panel) => panel.name === action.payload);
            if (sidePanel) {
                sidePanel.isOpen = false;
            } else {
                state.sidePanel.push({
                    name: action.payload,
                    isOpen: false,
                });
            }
        },
    },
});

export const { openSidePanel, closeSidePanel } = sidePanelSlice.actions;
export const selectSidePanel = (state: { sidePanel: SidePanelState }) => state.sidePanel.sidePanel;
export const sidePanelReducer = sidePanelSlice.reducer;
